import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

// Stable across page navigations within a single browser tab/session —
// the very first useCurrentInternalUser mount captures the existing
// internal_users.last_active_at as "previous active" for display, then
// bumps the DB column to NOW. Subsequent mounts in the same session
// reuse the stashed value so the banner doesn't flip to "just now" the
// moment the user navigates anywhere else.
const PREVIOUS_ACTIVE_KEY = 'sp_previous_active_at';

// Window event that every live useCurrentInternalUser hook listens for.
// Dispatched by code that mutates the internal_users row (e.g. an
// avatar change) so every consumer on the page can refresh in lockstep
// without each one having to wire its own refetch.
const USER_REFETCH_EVENT = 'sp:current-user-refetch';

// Fire-and-forget signal: I just changed something about the signed-in
// user's row, please pull fresh. Safe to call from anywhere.
export function notifyCurrentUserChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(USER_REFETCH_EVENT));
  }
}

export function useCurrentInternalUser() {
  const [user, setUser] = useState(null);
  const [previousActiveAt, setPreviousActiveAt] = useState(() => {
    // Seed synchronously from sessionStorage so the banner doesn't flash
    // a null state on the first render of every page in the session.
    if (typeof window === 'undefined') return null;
    const cached = window.sessionStorage.getItem(PREVIOUS_ACTIVE_KEY);
    return cached || null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        if (!cancelled) { setUser(null); setLoading(false); }
        return;
      }
      const { data } = await supabase
        .from('internal_users')
        .select('*')
        .eq('id', authUser.id)
        .single();
      if (cancelled) return;
      setUser(data || null);
      setLoading(false);
      // First mount of this session: stash the pre-existing last_active_at
      // and bump the DB to NOW. Fire-and-forget — failure shouldn't block
      // the page (the banner just falls back to whatever's in storage).
      const stashed = window.sessionStorage.getItem(PREVIOUS_ACTIVE_KEY);
      if (!stashed) {
        const prev = data?.last_active_at || '';
        window.sessionStorage.setItem(PREVIOUS_ACTIVE_KEY, prev);
        setPreviousActiveAt(prev || null);
        supabase
          .from('internal_users')
          .update({ last_active_at: new Date().toISOString() })
          .eq('id', authUser.id)
          .then(() => {});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cross-component refresh channel: when anything updates the
  // signed-in user's row (e.g. SidebarUserMenu saving a new avatar),
  // it dispatches the refetch event and every live hook instance
  // re-pulls. No last_active_at bump on these - that's only for the
  // initial-mount path above.
  useEffect(() => {
    let cancelled = false;
    const onRefetch = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser || cancelled) return;
      const { data } = await supabase
        .from('internal_users')
        .select('*')
        .eq('id', authUser.id)
        .single();
      if (cancelled) return;
      setUser(data || null);
    };
    window.addEventListener(USER_REFETCH_EVENT, onRefetch);
    return () => {
      cancelled = true;
      window.removeEventListener(USER_REFETCH_EVENT, onRefetch);
    };
  }, []);

  return { user, previousActiveAt, loading };
}

export const isFinance = (user) => user?.role === 'finance' || user?.role === 'admin';
export const isAdmin = (user) => user?.role === 'admin' || user?.role === 'super_user';
